import React, { useEffect, useLayoutEffect, useCallback, useContext, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { statusColor } from '../utils';
import { HeaderBack } from './HeaderBack';
export { HeaderBack };

// ─────────────────────────────────────────────────────────────
// SHARED UI LIBRARY · "Iris" overhaul
// Same exports + prop signatures as the original — every page
// keeps working. Entirely new visual language: soft rounded
// surfaces, violet brand, layered depth, airy data tables.
// ─────────────────────────────────────────────────────────────

const cvar = (name) => `var(--${name})`;
const mix = (name, pct) => `color-mix(in srgb, ${cvar(name)} ${pct}%, transparent)`;

// ─────────────────────────────────────────────
// SPINNER
// ─────────────────────────────────────────────
export const Spinner = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    style={{ animation: 'spin 0.7s linear infinite', display: 'block' }}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <circle cx="12" cy="12" r="9" stroke="var(--border-light)" strokeWidth="2.4" />
    <path d="M12 3a9 9 0 0 1 9 9" stroke="var(--accent)" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

// ─────────────────────────────────────────────
// RNL LOADER (BRANDED)
// ─────────────────────────────────────────────
export const RNLLoader = ({ title = 'Opening Project...', subtitle = 'Preparing your workspace', fullScreen = false }) => {
  const content = (
    <div style={{ background: '#395085ff', padding: '48px 64px', borderRadius: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', transform: fullScreen ? 'scale(1)' : 'scale(0.8)' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 240, height: 240, marginBottom: 24 }}>
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', animation: 'spin 1.2s linear infinite' }} viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="46" fill="none" stroke="#000104ff" strokeWidth="4" />
          <circle cx="50" cy="50" r="46" fill="none" stroke="#fbfcfcff" strokeWidth="4" strokeDasharray="144.5" strokeLinecap="round" />
        </svg>
        <img src="/rnl-logo.png" alt="Rifitel" style={{ width: 150, height: 150, borderRadius: 40, animation: 'pulse 1.6s ease-in-out infinite', boxShadow: 'var(--shadow)', position: 'relative', zIndex: 1 }} />
      </div>
      <style>{`
        @keyframes pulse{0%,100%{opacity:0.45;transform:scale(0.98)}50%{opacity:1;transform:scale(1)}}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>
      <h2 style={{ fontSize: 28, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.02em', margin: '0 0 8px 0' }}>{title}</h2>
      <p style={{ fontSize: 14, fontStyle: 'italic', color: '#cbd5e1', margin: 0 }}>{subtitle}</p>
    </div>
  );

  if (fullScreen) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(138, 167, 234, 0.2)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(7px)' }}>
        {content}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '60px 0' }}>
      {content}
    </div>
  );
};

// ─────────────────────────────────────────────
// BADGE — soft tinted pill
// ─────────────────────────────────────────────
const BADGE_COLORS = {
  accent: 'accent', green: 'green', amber: 'amber', red: 'red',
  purple: 'purple', cyan: 'cyan', blue: 'blue', indigo: 'indigo', gray: 'gray',
};

export const Badge = ({ children, color = 'gray', pulse = false, style: customStyle }) => {
  const key = BADGE_COLORS[color] || 'gray';
  const text = key === 'gray' ? 'var(--text-secondary)' : cvar(key);
  const borderColor = key === 'gray' ? 'var(--border-light)' : `color-mix(in srgb, ${cvar(key)} 55%, black 25%)`;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.005em',
      background: key === 'gray' ? 'var(--gray-dim)' : mix(key, 13),
      border: `1px solid ${borderColor}`,
      color: text, whiteSpace: 'nowrap', textTransform: 'capitalize',
      lineHeight: 1.5,
      ...customStyle,
    }}>
      {pulse && (
        <style>{`@keyframes badge-dot-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.35;transform:scale(1.7)}}`}</style>
      )}
      <span style={{
        width: 5, height: 5, borderRadius: '50%', background: 'currentColor', flexShrink: 0,
        animation: pulse ? 'badge-dot-pulse 1.1s ease-in-out infinite' : 'none',
      }} />
      {children}
    </span>
  );
};

export const StatusBadge = ({ value }) => (
  <Badge color={statusColor(value)}>{(value || '—').toString().replace(/_/g, ' ')}</Badge>
);

// ─────────────────────────────────────────────
// STATUS REASON
// The note that goes with a status: why an approver returned it, why it was
// rejected, or why it is on hold. Sits under the status badge in the lists so
// the reason travels with the state instead of only being visible once the
// request is opened.
// ─────────────────────────────────────────────
export const StatusReason = ({ reason, align = 'center' }) => {
  const text = (reason || '').trim();
  if (!text) return null;
  return (
    <div
      title={text}
      style={{
        marginTop: 4, fontStyle: 'italic', fontSize: 11, lineHeight: 1.4,
        color: 'var(--text-secondary)', fontWeight: 500,
        textAlign: align, maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word',
        marginLeft: align === 'center' ? 'auto' : undefined,
        marginRight: align === 'center' ? 'auto' : undefined,
      }}
    >
      “{text}”
    </div>
  );
};

// ─────────────────────────────────────────────
// RETURN DESTINATION NOTE
// Shown wherever a request that was returned for correction can be sent back
// on. One component so the inbox, the edit form and the details page cannot
// drift into describing the same action three different ways.
// ─────────────────────────────────────────────
export const ReturnDestinationNote = ({ stepName }) => (
  <div style={{
    fontSize: 13, color: '#475569', background: '#eff6ff',
    border: '1px solid #bfdbfe', borderRadius: 6, padding: '10px 12px', lineHeight: 1.55,
  }}>
    This request was sent back to you for correction. Submitting returns it
    to <strong>{stepName}</strong>, to the same approver who raised it.
  </div>
);

// ─────────────────────────────────────────────
// BACK BUTTON
// ─────────────────────────────────────────────
// The action now lives in the top bar (see HeaderBack): rendering this
// declares the view's Back rather than drawing an inline button. `style` is
// accepted and ignored so existing callers need no change.
export const BackButton = ({ onClick, children }) => (
  <HeaderBack onClick={onClick}>{children}</HeaderBack>
);

// ─────────────────────────────────────────────
// BUTTON
// ─────────────────────────────────────────────
const BTN_STYLES = {
  primary: { background: 'linear-gradient(180deg, var(--accent), var(--accent-hover))', color: '#fff', border: '1px solid transparent', boxShadow: '0 1px 0 rgba(255,255,255,0.22) inset, 0 8px 20px -6px var(--accent)' },
  secondary: { background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)' },
  ghost: { background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-light)' },
  danger: { background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid color-mix(in srgb, var(--red) 30%, transparent)' },
  success: { background: 'var(--green-dim)', color: 'var(--green)', border: '1px solid color-mix(in srgb, var(--green) 30%, transparent)' },
};

export const Btn = ({ children, onClick, variant = 'primary', size = 'sm', disabled, loading, style: s, type = 'button', title }) => {
  const vs = BTN_STYLES[variant] || BTN_STYLES.primary;
  const off = disabled || loading;
  return (
    <button type={type} onClick={onClick} disabled={off} title={title}
      onMouseDown={e => { if (!off) e.currentTarget.style.transform = 'translateY(1px)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      onMouseEnter={e => { if (!off) e.currentTarget.style.filter = 'brightness(1.05)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.filter = 'none'; }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        padding: size === 'sm' ? '8px 16px' : '11px 22px',
        borderRadius: 11, fontSize: 13, fontWeight: 700,
        cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1,
        transition: 'transform 0.08s ease, filter 0.15s ease',
        fontFamily: 'var(--font)', letterSpacing: '-0.01em',
        ...vs, ...s,
      }}>
      {loading && <Spinner size={14} />}
      {children}
    </button>
  );
};

// ─────────────────────────────────────────────
// FORM INPUTS
// ─────────────────────────────────────────────
const labelStyle = { fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700, display: 'block', marginBottom: 6, letterSpacing: '-0.005em' };
const inputBase = {
  background: 'var(--bg-base)', border: '1px solid var(--border-light)',
  borderRadius: 11, padding: '10px 13px', color: 'var(--text-primary)',
  fontSize: 13.5, width: '100%', fontFamily: 'var(--font)', outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
};
const focusOn = (e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = 'var(--ring)'; e.target.style.background = 'var(--bg-surface)'; };
const focusOff = (e) => { e.target.style.borderColor = 'var(--border-light)'; e.target.style.boxShadow = 'none'; e.target.style.background = 'var(--bg-base)'; };

export const Input = ({ label, value, onChange, onKeyDown, type = 'text', placeholder, required, readOnly, disabled, style: s, step, min, max, maxLength, as, rows }) => {
  const [showPw, setShowPw] = useState(false);
  if (as === 'textarea') {
    return <Textarea label={label} value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={s} readOnly={readOnly} disabled={disabled} />;
  }
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPw ? 'text' : 'password') : type;
  return (
    <div style={{ minWidth: 0 }}>
      {label && <label style={labelStyle}>{label}{required && <span style={{ color: 'var(--red)' }}> *</span>}</label>}
      <div style={{ position: 'relative' }}>
        <input type={inputType} value={value ?? ''} readOnly={readOnly} disabled={disabled}
          step={step} min={min} max={max} maxLength={maxLength}
          onChange={e => onChange && onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={readOnly ? undefined : focusOn} onBlur={focusOff}
          placeholder={placeholder}
          style={{ ...inputBase, ...((readOnly || disabled) ? { opacity: 0.85, background: '#e2e8f0', color: '#475569', cursor: 'not-allowed' } : {}), ...(isPassword ? { paddingRight: 40 } : {}), ...s }} />
        {isPassword && (
          <button type="button" onClick={() => setShowPw(!showPw)} style={{
            position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
            padding: 4, display: 'flex', alignItems: 'center', outline: 'none'
          }}>
            {showPw ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const DatePickerCustomHeader = ({
  date, changeYear, changeMonth, decreaseMonth, increaseMonth,
  prevMonthButtonDisabled, nextMonthButtonDisabled
}) => {
  const [showYearGrid, setShowYearGrid] = useState(false);
  const [gridCenter, setGridCenter] = useState(date.getFullYear());

  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const handleYearClick = (yr) => {
    changeYear(yr);
    setShowYearGrid(false);
  };

  const startYear = gridCenter - 7;
  const years = Array.from({ length: 16 }, (_, i) => startYear + i);

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'center', padding: '0 10px 10px' }}>
      <button
        onClick={(e) => { e.preventDefault(); decreaseMonth(); }}
        disabled={prevMonthButtonDisabled}
        style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: 16, color: '#475569' }}
      >
        ◀
      </button>

      <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
        <select
          value={months[date.getMonth()]}
          onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e1', cursor: 'pointer', outline: 'none' }}
        >
          {months.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <button
          onClick={(e) => { e.preventDefault(); setShowYearGrid(!showYearGrid); setGridCenter(date.getFullYear()); }}
          style={{ padding: '4px 12px', borderRadius: 4, border: '1px solid #cbd5e1', cursor: 'pointer', background: '#f8fafc', fontWeight: 600, outline: 'none' }}
        >
          {date.getFullYear()}
        </button>

        {showYearGrid && (
          <div style={{
            position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8, width: 240,
            background: 'white', border: '1px solid #cbd5e1', borderRadius: 8,
            zIndex: 99999, padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
              <button
                onClick={(e) => { e.preventDefault(); setGridCenter(gridCenter - 16); }}
                style={{ cursor: 'pointer', padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#f1f5f9' }}
              >
                ◀◀
              </button>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{years[0]} - {years[15]}</span>
              <button
                onClick={(e) => { e.preventDefault(); setGridCenter(gridCenter + 16); }}
                style={{ cursor: 'pointer', padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 4, background: '#f1f5f9' }}
              >
                ▶▶
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
              {years.map(yr => (
                <button
                  key={yr}
                  onClick={(e) => { e.preventDefault(); handleYearClick(yr); }}
                  style={{
                    padding: '6px 0',
                    border: 'none',
                    borderRadius: 4,
                    background: yr === date.getFullYear() ? '#0b318bff' : 'transparent',
                    color: yr === date.getFullYear() ? 'white' : '#334155',
                    cursor: 'pointer',
                    fontWeight: yr === date.getFullYear() ? 600 : 400,
                    fontSize: 13
                  }}
                >
                  {yr}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={(e) => { e.preventDefault(); increaseMonth(); }}
        disabled={nextMonthButtonDisabled}
        style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: 16, color: '#475569' }}
      >
        ▶
      </button>
    </div>
  );
};

// `portal`: the calendar renders on <body> (above modals), for screens whose animated containers would trap or clip it.
// `month`: a month picker — value and onChange use 'YYYY-MM'.
export const DatePicker = ({ label, value, onChange, required, readOnly, disabled, style: s, portal, month }) => {
  const selectedDate = value ? (month ? new Date(`${String(value).slice(0, 7)}-01T00:00:00`) : new Date(value)) : null;
  const handleChange = (date) => {
    if (onChange) {
      if (!date) onChange('');
      else if (month) onChange(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
      else {
        // Format to YYYY-MM-DD local time to prevent timezone shift issues
        const offset = date.getTimezoneOffset()
        const localDate = new Date(date.getTime() - (offset * 60 * 1000))
        onChange(localDate.toISOString().split('T')[0]);
      }
    }
  };

  return (
    <div style={{ minWidth: 0 }}>
      {label && <label style={labelStyle}>{label}{required && <span style={{ color: 'var(--red)' }}> *</span>}</label>}
      <div style={{ position: 'relative', width: '100%' }}>
        <ReactDatePicker
          selected={selectedDate}
          onChange={handleChange}
          readOnly={readOnly}
          disabled={disabled}
          dateFormat={month ? 'MMM yyyy' : 'dd/MM/yyyy'}
          showMonthYearPicker={!!month}
          wrapperClassName="w-100"
          // Line Items sits inside a scrollable, overflow-clipped table container —
          // 'fixed' positioning lets the popper escape that clipping instead of
          // being cut off/hidden under it, regardless of z-index.
          popperProps={{ strategy: 'fixed' }}
          portalId={portal ? 'app-datepicker-portal' : undefined}
          renderCustomHeader={month ? undefined : (props) => <DatePickerCustomHeader {...props} />}
          customInput={
            <input
              onFocus={readOnly ? undefined : focusOn} onBlur={focusOff}
              style={{ ...inputBase, width: '100%', boxSizing: 'border-box', ...((readOnly || disabled) ? { opacity: 0.85, background: '#e2e8f0', color: '#475569', cursor: 'not-allowed' } : {}), ...s }}
            />
          }
        />
      </div>
    </div>
  );
};

export const Textarea = ({ label, value, onChange, placeholder, rows = 3, style: s, readOnly, disabled }) => (
  <div style={{ minWidth: 0 }}>
    {label && <label style={labelStyle}>{label}</label>}
    <textarea value={value ?? ''} onChange={e => onChange && onChange(e.target.value)}
      onFocus={readOnly ? undefined : focusOn} onBlur={focusOff}
      placeholder={placeholder} rows={rows} readOnly={readOnly} disabled={disabled}
      style={{ ...inputBase, resize: 'vertical', lineHeight: 1.6, ...((readOnly || disabled) ? { opacity: 0.85, background: '#e2e8f0', color: '#475569', cursor: 'not-allowed' } : {}), ...s }} />
  </div>
);

// `title` names the modal without printing a visible <label> — the grid
// cells in the requisition line items have their heading in the table
// header already, but still want the picker to say what it is picking.
// `placeholder` is the text shown while nothing is selected.
const wrapPortal = (portal, node) => (portal ? createPortal(node, document.body) : node);

// `portal`: the picker opens on <body>, above any modal, for screens whose animated containers would trap a fixed overlay
export const Select = ({ label, title, value, onChange, options = [], required, style: s, disabled, placeholder, modalWidth, portal }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOption = options.find(o => String(o.value) === String(value));
  const displayLabel = selectedOption
    ? selectedOption.label
    : (options.find(o => String(o.value) === '')?.label || placeholder || '— Select —');

  // `alwaysVisible` survives the search box. Without it, typing a material
  // that is not in the list filtered away the "Others" escape hatch at the
  // exact moment somebody needed it — an empty list and no way out.
  const filteredOptions = options.filter(o =>
    o.alwaysVisible || (o.label || '').toLowerCase().includes((search || '').toLowerCase()));

  return (
    <div style={{ minWidth: 0 }}>
      {label && <label style={labelStyle}>{label}{required && <span style={{ color: 'var(--red)' }}> *</span>}</label>}
      <div
        onClick={() => !disabled && setOpen(true)}
        style={{
          ...inputBase,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? '#e2e8f0' : 'var(--bg-base)',
          color: disabled ? '#475569' : 'var(--text-primary)',
          opacity: disabled ? 0.85 : 1,
          ...s
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginLeft: 8 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {open && !disabled && wrapPortal(portal,
        <Modal title={title || label || "Select Option"} onClose={() => setOpen(false)} width={modalWidth || 650} blur={1} headerBg="#0b318bff" headerColor="#ffffff">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Counted on the real choices, not the list length: a leading
                "Select" placeholder is not something anyone searches for, and
                counting it meant a picker with four genuine options (the
                project list, for one) sat just under the threshold and
                offered no search at all. */}
            {options.filter((o) => o.value !== '' && o.value != null).length > 3 && (
              <Input
                placeholder="Search options..."
                value={search}
                onChange={setSearch}
                style={{ background: 'var(--bg-surface)' }}
              />
            )}
            <div style={{ maxHeight: '35vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 4 }}>
              {filteredOptions.length > 0 ? filteredOptions.map((o, _i) => {
                const isSelected = String(o.value) === String(value);
                return (
                  <div
                    key={o.value != null ? o.value : `__opt_${_i}`}
                    onClick={() => { onChange && onChange(o.value); setOpen(false); setSearch(''); }}
                    style={{
                      padding: '12px 16px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: isSelected ? 'var(--bg-hover)' : 'transparent',
                      color: isSelected ? 'var(--accent)' : 'var(--text-primary)',
                      fontWeight: isSelected ? 700 : 500,
                      fontSize: 13.5,
                      border: '1px solid var(--border-light)',
                      transition: 'background 0.1s',
                      wordBreak: 'break-word',
                      textAlign: 'left',
                    }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {o.label}
                  </div>
                );
              }) : (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No options found matching "{search}"
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// SEARCHABLE SELECT
// Plain inline dropdown (not a popup modal, like Select above) with a search
// box baked into the open panel — for grid/table dropdowns with long option
// lists where scanning unfiltered options is slow.
//
// The open panel renders through a portal straight into document.body rather
// than as a normal absolutely-positioned child. Raising z-index alone doesn't
// reliably win against later page content (e.g. a totals footer) once any
// ancestor between the trigger and the page root creates its own stacking
// context — that context caps how far z-index can reach. A portal sidesteps
// the whole problem: the panel is no longer inside that ancestor chain at all.
//
// It also flips to open upward instead of downward when there isn't enough
// room below the trigger (e.g. a row near the bottom of the viewport) — the
// panel first renders in its default "below" spot, then a layout effect
// measures its real height and repositions it above if that fits better,
// before the browser paints, so there's no visible jump.
// ─────────────────────────────────────────────
export const SearchableSelect = ({ value, onChange, options = [], placeholder = '— Select —', style: s, disabled, dropdownWidth = 240 }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [panelPos, setPanelPos] = useState(null);
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const openPanel = () => {
    if (disabled) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPos({ top: rect.bottom + 6, left: rect.left, width: Math.max(rect.width, dropdownWidth), placement: 'below' });
    setHighlighted(0);
    setOpen(true);
  };
  const closePanel = () => { setOpen(false); setQuery(''); };

  // Flip to "above" if the panel doesn't fit below but does fit above.
  // useLayoutEffect (not useEffect) so this is measured and corrected before
  // the browser paints — otherwise there'd be a visible flash in the wrong spot.
  useLayoutEffect(() => {
    if (!open || !panelRef.current || !triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const panelHeight = panelRef.current.getBoundingClientRect().height;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    if (spaceBelow < panelHeight + 10 && spaceAbove > spaceBelow) {
      setPanelPos((prev) => (prev && prev.placement !== 'above'
        ? { ...prev, top: triggerRect.top - panelHeight - 6, placement: 'above' }
        : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      closePanel();
    };
    // The panel is fixed-positioned from a rect snapshot taken on open —
    // scrolling the page or a table's own scroll container (hence
    // capture:true, to catch those too) would leave it visually stranded,
    // so close it — but scrolling the panel's own option list is normal use
    // of the dropdown, not a reason to close it, so that one's excluded.
    const handleScroll = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      closePanel();
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', closePanel);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', closePanel);
    };
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = query
    ? options.filter((o) => (o.label || '').toLowerCase().includes(query.toLowerCase()))
    : options;

  const pick = (o) => { onChange(o.value); closePanel(); };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { closePanel(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => Math.min(i + 1, filtered.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlighted]) pick(filtered[highlighted]); }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth: 0 }}>
      <div
        ref={triggerRef}
        onClick={() => (open ? closePanel() : openPanel())}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? '#f1f5f9' : '#fff',
          opacity: disabled ? 0.7 : 1,
          border: `1px solid ${open ? '#2563eb' : '#cbd5e1'}`, borderRadius: 6, padding: '4px 6px', fontSize: 11.5,
          boxSizing: 'border-box',
          boxShadow: open ? '0 0 0 3px rgba(37,99,235,0.12)' : 'none',
          transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
          ...s,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? '#0f172a' : '#94a3b8' }}>
          {selected ? selected.label : placeholder}
        </span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ flexShrink: 0, opacity: 0.55, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {open && !disabled && panelPos && createPortal(
        <div
          ref={panelRef}
          onKeyDown={handleKeyDown}
          style={{
            position: 'fixed', top: panelPos.top, left: panelPos.left, zIndex: 9999,
            width: panelPos.width,
            background: '#fff', border: '1px solid #d7dce3', borderRadius: 10,
            boxShadow: '0 12px 32px -8px rgba(15,23,42,0.28), 0 2px 8px rgba(15,23,42,0.08)',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'relative', padding: 8, borderBottom: '1px solid #edf0f4' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.3"
              style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlighted(0); }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              placeholder="Search..."
              style={{
                width: '100%', boxSizing: 'border-box', padding: '8px 26px 8px 30px',
                border: '1px solid #d7dce3', borderRadius: 7, fontSize: 12.5, outline: 'none',
                color: '#0f172a',
              }}
              onFocus={(e) => { e.target.style.borderColor = '#2563eb'; }}
              onBlur={(e) => { e.target.style.borderColor = '#d7dce3'; }}
            />
            {query && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setQuery(''); setHighlighted(0); }}
                title="Clear search"
                style={{
                  position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                  border: 'none', background: 'none', cursor: 'pointer', padding: 4,
                  display: 'flex', color: '#94a3b8',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          <div style={{ maxHeight: 224, overflowY: 'auto', padding: '4px 0' }}>
            {filtered.length > 0 ? filtered.map((o, idx) => {
              const isSelected = String(o.value) === String(value);
              const isHighlighted = idx === highlighted;
              return (
                <div
                  key={o.value != null ? o.value : `__opt_${idx}`}
                  onClick={() => pick(o)}
                  onMouseEnter={() => setHighlighted(idx)}
                  style={{
                    padding: '8px 14px', fontSize: 12.5, cursor: 'pointer',
                    background: isHighlighted ? '#eff3fb' : '#fff',
                    color: isSelected ? '#1d4ed8' : '#1e293b',
                    fontWeight: isSelected ? 700 : 500,
                    borderLeft: isSelected ? '3px solid #2563eb' : '3px solid transparent',
                    transition: 'background 0.08s ease',
                  }}
                >
                  {o.label}
                </div>
              );
            }) : (
              <div style={{ padding: '18px 10px', fontSize: 12.5, color: '#94a3b8', textAlign: 'center' }}>No matches found</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// CARD
// ─────────────────────────────────────────────
export const Card = ({ children, style: s, onClick, className, onMouseEnter, onMouseLeave }) => {
  const interactive = !!onClick;
  // Directional borders count too. The guard only looked at `border` and
  // `borderColor`, so a card styled with `borderLeft` was treated as having no
  // custom border — and the hover handler then set `borderColor` on all four
  // sides, wiping the accent stripe the card was defined by.
  const hasCustomBorder = !!(
    s?.border || s?.borderColor ||
    s?.borderLeft || s?.borderRight || s?.borderTop || s?.borderBottom ||
    s?.borderLeftColor || s?.borderRightColor || s?.borderTopColor || s?.borderBottomColor
  );
  return (
    <div onClick={onClick} className={className}
      onMouseEnter={onMouseEnter || (interactive ? (e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; if (!hasCustomBorder) e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) 35%, var(--border))'; }) : undefined)}
      onMouseLeave={onMouseLeave || (interactive ? (e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; if (!hasCustomBorder) e.currentTarget.style.borderColor = 'var(--border)'; }) : undefined)}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid black',
        borderRadius: 10,
        boxShadow: 'var(--shadow-sm)',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
        ...s,
        ...(interactive ? { cursor: 'pointer' } : {}),
      }}>{children}</div>
  );
};

// ─────────────────────────────────────────────
// MODAL
// ─────────────────────────────────────────────
export const Modal = ({ title, onClose, children, width = 620, minHeight, hideHeader = false, blur = 7, headerBg = '#0b318bff', headerColor = '#ffffff' }) => (
  // The backdrop deliberately has no click handler. It used to close the modal
  // on any click that landed outside the frame, which meant a mis-aimed click
  // silently threw away a half-filled form or an in-progress approval. Closing
  // is now an explicit act: the × in the header, or a button in the body.
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(105, 116, 143, 0.35)',
    backdropFilter: `blur(${blur}px)`, WebkitBackdropFilter: `blur(${blur}px)`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 10, animation: 'mdlBg 0.2s ease',
  }}>
    {/* The rounded frame no longer scrolls — the body inside it does.
        When this element was the scroll container, an overflowing modal put a
        scrollbar hard against the right edge and squared off both right
        corners, so the border radius only showed on the left. overflow:hidden
        here keeps the radius clipping cleanly whatever the content height. */}
    <div style={{
      background: 'var(--bg-card)', border: '3px solid black',
      borderRadius: 22, width: '100%', maxWidth: width,
      maxHeight: '92vh', minHeight: minHeight || undefined, overflow: 'hidden', boxShadow: 'var(--shadow-lg)',
      display: 'flex', flexDirection: 'column',
      animation: 'mdlPop 0.26s cubic-bezier(0.22,1,0.36,1)',
    }}>
      <style>{`
        @keyframes mdlBg{from{opacity:0}to{opacity:1}}
        @keyframes mdlPop{from{opacity:0;transform:translateY(16px) scale(0.98)}to{opacity:1;transform:none}}
      `}</style>
      {!hideHeader && (
        <div style={{ padding: '18px 24px', borderBottom: '2px solid black', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: headerBg, flex: '0 0 auto' }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: headerColor }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{
            background: 'var(--red)', border: '1px solid var(--red)', color: '#ffffff',
            fontSize: 18, cursor: 'pointer', lineHeight: 1, width: 32, height: 32, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
          }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 12px rgba(240, 57, 91, 0.6)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'scale(1)'; }}
          >×</button>
        </div>
      )}
      <div style={{ padding: 18, overflow: 'auto', flex: '1 1 auto' }}>{children}</div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// TABLE
// ─────────────────────────────────────────────
export const Table = ({ cols, rows, loading, emptyMsg = 'No records found.', maxHeight }) => (
  <div className="table-responsive-wrapper" style={{ overflowY: maxHeight ? 'auto' : undefined, overflowX: 'auto', maxHeight: maxHeight || undefined, borderRadius: 0 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={c.key} style={{
              padding: '11px 14px', textAlign: c.align || 'left',
              fontSize: 9.5, fontWeight: 800, color: 'white',
              textTransform: 'uppercase', letterSpacing: '0.09em', whiteSpace: 'nowrap',
              background: '#0b318bff',
              border: '1px solid black',
              position: 'sticky', top: 0,
              left: c.stickyLeft !== undefined ? c.stickyLeft : undefined,
              zIndex: c.stickyLeft !== undefined ? 3 : 1,
              width: c.width,
              minWidth: c.minWidth || c.width,
              maxWidth: c.maxWidth,
            }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          Array(5).fill(0).map((_, i) => (
            <tr key={`loading-${i}`}>
              {cols.map((c, j) => (
                <td key={`loading-${i}-${j}`} style={{
                  padding: '11px 14px',
                  border: '1px solid black',
                  background: i % 2 === 0 ? '#fff' : '#f8fafc',
                  position: c.stickyLeft !== undefined ? 'sticky' : undefined,
                  left: c.stickyLeft !== undefined ? c.stickyLeft : undefined,
                  zIndex: c.stickyLeft !== undefined ? 1 : undefined,
                  width: c.width, minWidth: c.minWidth || c.width, maxWidth: c.maxWidth
                }}>
                  <div style={{
                    height: 20,
                    borderRadius: 4,
                    backgroundColor: 'var(--bg-card)',
                    animation: 'shimmer 1.5s infinite linear',
                    backgroundImage: 'linear-gradient(90deg, var(--bg-card) 10%, var(--border-light) 50%, var(--bg-card) 90%)',
                    backgroundSize: '200% 100%'
                  }} />
                </td>
              ))}
            </tr>
          ))
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={cols.length} style={{ padding: '56px 18px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              <div style={{ fontSize: 26, marginBottom: 10, opacity: 0.4 }}>⌗</div>
              {emptyMsg}
            </td>
          </tr>
        ) : rows.map((row, i) => (
          row._isGroupHeader ? (
            <tr key={row.id || `group-${i}`}>
              <td colSpan={cols.length} style={{ padding: '12px 16px', position: 'sticky', left: 0, zIndex: 1, background: '#d1d5db', border: '1px solid black' }}>
                {row.content}
              </td>
            </tr>
          ) : (
            <tr key={row.id || i} style={{ transition: 'background 0.12s', background: row._bgColor || (i % 2 === 0 ? '#fff' : '#f8fafc'), fontStyle: row._fontStyle || 'normal' }}
              onMouseEnter={e => e.currentTarget.style.background = row._hoverBgColor || '#dbeafe'}
              onMouseLeave={e => e.currentTarget.style.background = row._bgColor || (i % 2 === 0 ? '#fff' : '#f8fafc')}>
              {cols.map(c => (
                <td key={c.key} style={{
                  padding: '11px 14px', fontSize: 11.5, color: 'var(--text-primary)', textAlign: c.align || 'left', whiteSpace: c.nowrap ? 'nowrap' : undefined,
                  position: c.stickyLeft !== undefined ? 'sticky' : undefined,
                  left: c.stickyLeft !== undefined ? c.stickyLeft : undefined,
                  zIndex: c.stickyLeft !== undefined ? 1 : undefined,
                  background: c.stickyLeft !== undefined ? 'inherit' : undefined,
                  border: '1px solid black',
                  width: c.width,
                  minWidth: c.minWidth || c.width,
                  maxWidth: c.maxWidth
                }}>
                  {c.render ? c.render(row[c.key], row, i) : row[c.key] ?? '—'}
                </td>
              ))}
            </tr>
          )
        ))}
      </tbody>
    </table>
  </div>
);

// ─────────────────────────────────────────────
// STAT CARD — soft aura + tinted icon chip
// ─────────────────────────────────────────────
export const StatCard = ({ label, value, sub, color = 'accent', icon, style: s, className }) => (
  <Card className={className} style={{ padding: '18px 20px', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%', ...s, border: '2px solid black' }}>
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: 12, flex: 1 }}>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'black', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{label}</div>
        <div style={{ marginTop: 'auto' }}>
          <div style={{ fontSize: 25, fontWeight: 500, color: cvar(color), fontFamily: 'var(--mono)', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.05 }}>{value ?? '—'}</div>
          {sub && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 7 }}>{sub}</div>}
        </div>
      </div>
      {icon && (
        <div style={{
          width: 42, height: 42, flexShrink: 0, borderRadius: 13,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 19, background: mix(color, 15), color: cvar(color),
        }}>{icon}</div>
      )}
    </div>
  </Card>
);

// ─────────────────────────────────────────────
// PROGRESS BAR
// ─────────────────────────────────────────────
export const ProgressBar = ({ value, max = 100, color = 'accent' }) => {
  const pct = max > 0 ? Math.min((parseFloat(value) / parseFloat(max)) * 100, 100) : 0;
  const c = pct > 90 ? 'red' : pct > 70 ? 'amber' : color;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 7, background: 'var(--bg-hover)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${mix(c, 70)}, ${cvar(c)})`, borderRadius: 999, transition: 'width 0.6s cubic-bezier(0.22,1,0.36,1)' }} />
      </div>
      <span style={{ fontSize: 10.5, color: 'var(--text-secondary)', minWidth: 32, textAlign: 'right', fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{pct.toFixed(0)}%</span>
    </div>
  );
};

// ─────────────────────────────────────────────
// SECTION HEADER  (title intentionally shown in TopBar)
// ─────────────────────────────────────────────
export const SectionHeader = ({ title, subtitle, action }) => {
  if (!subtitle && !action) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22, gap: 12 }}>
      <div>
        {subtitle && <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 2 }}>{subtitle}</p>}
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────
// FORM GRID
// ─────────────────────────────────────────────
export const FormGrid = ({ children, cols = 2 }) => (
  <div className={`grid-cols-${cols}`}>
    {children}
  </div>
);

// ─────────────────────────────────────────────
// TABS — segmented soft control
// ─────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div style={{
    display: 'flex', marginBottom: 0,
    maxWidth: '100%', overflowX: 'auto',
  }}>
    {tabs.map(t => {
      const on = active === t.id;
      return (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: '6px 14px',
          background: on ? '#0b318bff' : 'transparent',
          border: '1px solid black',
          borderBottom: 'none',
          borderRadius: '8px 8px 0 0',
          color: on ? '#ffffff' : '#64748b',
          fontSize: 11.5, fontWeight: 700,
          marginRight: 4,
          cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.2s ease',
          whiteSpace: 'nowrap',
          display: 'flex', alignItems: 'center', gap: 6
        }}
          onMouseEnter={e => { if (!on) { e.currentTarget.style.color = '#334155'; e.currentTarget.style.background = '#f8fafc'; } }}
          onMouseLeave={e => { if (!on) { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = 'transparent'; } }}
        >
          {t.icon && <span style={{ fontSize: 11 }}>{t.icon}</span>}
          {t.label}
        </button>
      );
    })}
  </div>
);

// ─────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────
const ToastItem = ({ msg, type, onClose }) => {
  // 4s is fine for "Saved", far too short for a validation error naming three
  // fields and the rule each one broke. Give long messages time to be read,
  // within reason.
  //
  // Raised on request: the floor from 4s to 6s and the ceiling from 14s to 20s.
  // The slow-request notice is the case that drove it — it appears while the
  // reader is watching a spinner and wondering whether to click again, which is
  // the worst possible moment for the explanation to vanish.
  useEffect(() => {
    const ms = Math.min(20000, 6000 + String(msg || '').length * 55);
    const t = setTimeout(onClose, ms);
    return () => clearTimeout(t);
  }, [onClose, msg]);
  const map = { success: 'green', error: 'red', info: 'accent' };
  const icons = { success: '✓', error: '✕', info: 'ℹ' };
  const key = map[type] || 'accent';
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-light)',
      borderRadius: 14, padding: '14px 16px', maxWidth: 420,
      boxShadow: 'var(--shadow)', fontSize: 13, color: 'var(--text-primary)',
      display: 'flex', alignItems: 'flex-start', gap: 12,
      animation: 'slideIn 0.26s cubic-bezier(0.22,1,0.36,1)',
    }}>
      <style>{`@keyframes slideIn{from{opacity:0;transform:translateX(22px)}to{opacity:1;transform:none}}`}</style>
      <span style={{
        width: 22, height: 22, borderRadius: 8, flexShrink: 0, marginTop: 1,
        background: mix(key, 16), color: cvar(key),
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12,
      }}>{icons[type] || icons.info}</span>
      {/* pre-line so a multi-field validation error stays one field per line
          instead of collapsing into a run-on sentence. */}
      <span style={{ flex: 1, lineHeight: 1.5, fontWeight: 500, whiteSpace: 'pre-line' }}>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 17, lineHeight: 1, padding: 0, marginTop: 1 }}>×</button>
    </div>
  );
};

export const ToastContainer = ({ toasts, remove }) => (
  <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10 }}>
    {toasts.map(t => <ToastItem key={t.id} {...t} onClose={() => remove(t.id)} />)}
  </div>
);

// Shared so every caller pushes into the ONE stack that is actually rendered.
//
// useToast used to create its own local state per component. Only App rendered
// a ToastContainer, bound to App's own instance — so every showToast() call in
// a page or component wrote into an array nothing displayed, and its messages
// were silently dropped. Error toasts in particular: a failed save reported
// nothing at all.
const ToastContext = React.createContext(null);

export const ToastProvider = ({ children }) => {
  const value = useToastState();

  // A request that passes the halfway mark says so, rather than leaving a
  // two-minute wait looking like a hang. Listened for here because this is the
  // one component mounted for the whole app — api.js dispatches a DOM event and
  // knows nothing about toasts.
  //
  // Multiple endpoints may all be slow at once; we show one notice regardless.
  // Use a synchronous ref flag — checking toast state is unreliable because React
  // state updates haven't flushed by the time the next event fires.
  const slowActiveRef = React.useRef(false);
  useEffect(() => {
    const onSlow = (e) => {
      if (slowActiveRef.current) return;
      slowActiveRef.current = true;
      const msg = e.detail?.mutating
        ? 'Still Submitting - Your request is taking too long, possible due to poor network, please kindly wait'
        : 'Still Loading - Your request is taking too long, possible due to poor network, please kindly wait';
      value.show(msg, 'info');
      // Reset after the maximum toast lifetime so a genuinely new slow wave can notify again.
      setTimeout(() => { slowActiveRef.current = false; }, 22000);
    };
    window.addEventListener('api:slow-request', onSlow);
    return () => window.removeEventListener('api:slow-request', onSlow);
  }, [value]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={value.toasts} remove={value.remove} />
    </ToastContext.Provider>
  );
};

const useToastState = () => {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(ts => [...ts, { id, msg, type }]);
  }, []);
  const remove = useCallback((id) => setToasts(ts => ts.filter(t => t.id !== id)), []);
  return { toasts, show, remove };
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  // Falls back to local state outside a provider so nothing breaks, but inside
  // the app the provider is always there and every toast reaches the screen.
  const local = useToastState();
  return ctx || local;
};


/**
 * Shown beside a long-running action once it has taken longer than expected.
 *
 * A spinner alone cannot distinguish "working" from "stuck", which is exactly
 * the ambiguity that makes a slow submit feel broken. This says so after a few
 * seconds, so the wait is explained rather than silent.
 */
export const SlowRequestHint = ({ active, after = 8000, children }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!active) { setShow(false); return; }
    const t = setTimeout(() => setShow(true), after);
    return () => clearTimeout(t);
  }, [active, after]);
  if (!show) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8,
      marginTop: 12, padding: '9px 12px', borderRadius: 8,
      background: 'var(--amber-dim)', border: '1px solid rgba(245,158,11,0.3)',
      fontSize: 12.5, color: 'var(--amber)', lineHeight: 1.55,
    }}>
      <span>&#9203;</span>
      <span>{children || 'This is taking longer than usual — check your connection. It will stop on its own if the server cannot be reached.'}</span>
    </div>
  );
};

// ─────────────────────────────────────────────
// EMPTY STATE
// ─────────────────────────────────────────────
export const EmptyState = ({ icon = '📭', title = 'No data', subtitle, action }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '66px 20px', textAlign: 'center' }}>
    <div style={{
      width: 66, height: 66, borderRadius: 20, marginBottom: 18,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28,
      background: 'var(--accent-dim)', color: 'var(--accent)',
    }}>{icon}</div>
    <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6, color: 'var(--text-primary)' }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13.5, color: 'var(--text-muted)', maxWidth: 340, lineHeight: 1.6 }}>{subtitle}</div>}
    {action && <div style={{ marginTop: 18 }}>{action}</div>}
  </div>
);

// ─────────────────────────────────────────────
// PROJECT REQUIRED GUARD
// ─────────────────────────────────────────────
export const NeedProject = () => (
  <EmptyState icon="🏗" title="Select a project" subtitle="Choose a project from the dropdown at the top to get started." />
);
